import type { WebhookActivityResponse, WebhookSourceSummary } from '@/lib/types';
import { CircleDashed, RefreshCw, Webhook } from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { Button } from '@reflex/ui/components/ui/button';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { formatTime, timeAgo } from '@reflex/ui/lib/format';

function WebhookSourceRow({ item }: { item: WebhookSourceSummary }) {
  const hasActivity = item.lastReceivedAt !== null;
  const hasErrors = item.totalErrors > 0;

  return (
    <div className="py-1">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground capitalize">{item.source}</span>
        {hasActivity ? (
          <span className="flex items-center gap-1.5 text-xs">
            <span className={hasErrors ? 'text-status-warning' : 'text-status-success'}>
              {item.totalReceived}
            </span>
            {hasErrors && <span className="text-status-error">({item.totalErrors} err)</span>}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <CircleDashed className="h-3 w-3 shrink-0" />
            none
          </span>
        )}
      </div>
      {hasActivity && (
        <div className="text-3xs text-muted-foreground mt-0.5 pl-0.5">
          last: {timeAgo(item.lastReceivedAt!)}
        </div>
      )}
      {item.lastError && (
        <div
          className="text-3xs text-status-error/80 mt-0.5 pl-0.5 truncate"
          title={item.lastError.message}
        >
          {item.lastError.message}
        </div>
      )}
    </div>
  );
}

export function WebhookActivitySection({
  activity,
  isLoading,
  onRefresh,
}: {
  activity: WebhookActivityResponse | undefined;
  isLoading: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <SectionLabel as="h4" className="flex items-center gap-1.5">
          <Webhook className="h-3.5 w-3.5" />
          Webhook Activity
        </SectionLabel>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 gap-1 text-3xs text-muted-foreground"
          onClick={onRefresh}
          disabled={isLoading}
        >
          {isLoading ? <Spinner size="xs" /> : <RefreshCw className="h-3 w-3" />}
          {isLoading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>
      <div className="rounded-md border border-border bg-muted/20 p-3 space-y-1">
        {!activity && !isLoading && (
          <div className="text-xs text-muted-foreground text-center py-1">
            Click &ldquo;Refresh&rdquo; to load webhook activity
          </div>
        )}
        {isLoading && !activity && (
          <div className="flex items-center justify-center gap-2 py-1">
            <Spinner className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        )}
        {activity?.sources.map((source) => (
          <WebhookSourceRow key={source.source} item={source} />
        ))}
      </div>
      {activity && (
        <div className="text-3xs text-muted-foreground text-right">
          tracking since {formatTime(activity.serverStartedAt)}
        </div>
      )}
    </div>
  );
}
